// Shared build-time context + geometry accumulators for the city module.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { AABB, PointOfInterest } from './types';
import type { Rng } from './util';
import type { Textures, FacadeStyle } from './city_textures';
import type { Layout } from './city_layout';

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _e = new THREE.Euler();

/** compose a matrix: translation, yaw, scale (fresh object each call: build-time only) */
export function mat(x: number, y: number, z: number, ry = 0, sx = 1, sy = 1, sz = 1, rx = 0, rz = 0): THREE.Matrix4 {
  _e.set(rx, ry, rz, 'YXZ');
  _q.setFromEuler(_e);
  _p.set(x, y, z);
  _s.set(sx, sy, sz);
  return new THREE.Matrix4().compose(_p, _q, _s);
}

/** Accumulates vertex-coloured geometries; merged into one mesh at the end. */
export class Bucket {
  parts: THREE.BufferGeometry[] = [];
  count = 0;
  constructor(public name: string) {}
  add(g: THREE.BufferGeometry, m: THREE.Matrix4 | null, color: number | THREE.Color): void {
    const geo = g.index ? g.toNonIndexed() : g;
    if (m) geo.applyMatrix4(m);
    const c = color instanceof THREE.Color ? color : new THREE.Color(color);
    const n = geo.attributes.position.count;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      arr[i * 3] = c.r;
      arr[i * 3 + 1] = c.g;
      arr[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
    if (!geo.attributes.uv) geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    for (const k of Object.keys(geo.attributes)) if (k !== 'position' && k !== 'normal' && k !== 'uv' && k !== 'color') geo.deleteAttribute(k);
    this.parts.push(geo);
    this.count++;
  }
  box(x: number, y: number, z: number, w: number, h: number, d: number, color: number | THREE.Color, ry = 0): void {
    this.add(new THREE.BoxGeometry(w, h, d), mat(x, y + h / 2, z, ry), color);
  }
  cyl(x: number, y: number, z: number, rt: number, rb: number, h: number, color: number | THREE.Color, seg = 16): void {
    this.add(new THREE.CylinderGeometry(rt, rb, h, seg), mat(x, y + h / 2, z), color);
  }
  cone(x: number, y: number, z: number, r: number, h: number, color: number | THREE.Color, seg = 12): void {
    this.add(new THREE.ConeGeometry(r, h, seg), mat(x, y + h / 2, z), color);
  }
  dome(x: number, y: number, z: number, r: number, color: number | THREE.Color, seg = 20, sy = 1): void {
    this.add(new THREE.SphereGeometry(r, seg, Math.max(6, seg >> 1), 0, Math.PI * 2, 0, Math.PI / 2), mat(x, y, z, 0, 1, sy, 1), color);
  }
  sphere(x: number, y: number, z: number, r: number, color: number | THREE.Color, seg = 12): void {
    this.add(new THREE.SphereGeometry(r, seg, Math.max(6, seg >> 1)), mat(x, y, z), color);
  }
  build(): THREE.BufferGeometry | null {
    if (!this.parts.length) return null;
    const g = mergeGeometries(this.parts, false);
    for (const p of this.parts) p.dispose();
    this.parts = [];
    if (!g) return null;
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

/** Quad soup with explicit UVs + vertex colours (textured surfaces: facades, roads, roofs). */
export class QuadSoup {
  pos: number[] = [];
  nor: number[] = [];
  uv: number[] = [];
  col: number[] = [];
  quads = 0;
  constructor(public name: string) {}
  /** a,b,c,d counter-clockwise seen from the front; uvs per corner; colour linear rgb */
  quad(
    ax: number, ay: number, az: number, bx: number, by: number, bz: number, cx: number, cy: number, cz: number, dx: number, dy: number, dz: number,
    ua: number, va: number, ub: number, vb: number, uc: number, vc: number, ud: number, vd: number, r = 1, g = 1, b = 1,
  ): void {
    // normal
    const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
    const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
    let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l; ny /= l; nz /= l;
    const P = this.pos, N = this.nor, U = this.uv, C = this.col;
    P.push(ax, ay, az, bx, by, bz, cx, cy, cz, ax, ay, az, cx, cy, cz, dx, dy, dz);
    for (let i = 0; i < 6; i++) {
      N.push(nx, ny, nz);
      C.push(r, g, b);
    }
    U.push(ua, va, ub, vb, uc, vc, ua, va, uc, vc, ud, vd);
    this.quads++;
  }
  /** horizontal quad (facing +Y) covering [x0,x1]x[z0,z1] at height y with world-tiled uv (metres per tile) */
  floor(x0: number, z0: number, x1: number, z1: number, y: number, tile: number, r = 1, g = 1, b = 1): void {
    const u0 = x0 / tile, u1 = x1 / tile, v0 = z0 / tile, v1 = z1 / tile;
    this.quad(x0, y, z1, x1, y, z1, x1, y, z0, x0, y, z0, u0, v1, u1, v1, u1, v0, u0, v0, r, g, b);
  }
  /** axis-aligned box with all 6 faces, world-tiled uvs (for slabs/curbs) */
  box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, tile: number, r = 1, g = 1, b = 1, top = true, bottom = false): void {
    if (top) this.floor(x0, z0, x1, z1, y1, tile, r, g, b);
    if (bottom) this.quad(x0, y0, z0, x1, y0, z0, x1, y0, z1, x0, y0, z1, 0, 0, 1, 0, 1, 1, 0, 1, r, g, b);
    const h = (y1 - y0) / tile;
    // +z
    this.quad(x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1, x0 / tile, 0, x1 / tile, 0, x1 / tile, h, x0 / tile, h, r, g, b);
    // -z
    this.quad(x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0, x1 / tile, 0, x0 / tile, 0, x0 / tile, h, x1 / tile, h, r, g, b);
    // +x
    this.quad(x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1, z1 / tile, 0, z0 / tile, 0, z0 / tile, h, z1 / tile, h, r, g, b);
    // -x
    this.quad(x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0, z0 / tile, 0, z1 / tile, 0, z1 / tile, h, z0 / tile, h, r, g, b);
  }
  build(): THREE.BufferGeometry | null {
    if (!this.quads) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(this.nor), 3));
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(this.uv), 2));
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.col), 3));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    this.pos = [];
    this.nor = [];
    this.uv = [];
    this.col = [];
    return g;
  }
}

/** 4 textured side faces of a box (no top / bottom). uv in metres / tile, u increases to the viewer's right. */
export function facadeBox(soup: QuadSoup, st: FacadeStyle, x0: number, z0: number, x1: number, z1: number, y0: number, y1: number, uOff: number, vOff: number, tint: THREE.Color): void {
  const w = x1 - x0, d = z1 - z0, h = y1 - y0;
  const tw = st.tileW, th = st.tileH;
  const v0 = vOff / th, v1 = (vOff + h) / th;
  const r = tint.r, g = tint.g, b = tint.b;
  // +z face (viewer at +z): u along +x
  soup.quad(x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1, uOff / tw, v0, (uOff + w) / tw, v0, (uOff + w) / tw, v1, uOff / tw, v1, r, g, b);
  // -z face: u along -x
  soup.quad(x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0, (uOff + w) / tw, v0, (uOff + 2 * w) / tw, v0, (uOff + 2 * w) / tw, v1, (uOff + w) / tw, v1, r, g, b);
  // +x face: u along -z
  soup.quad(x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1, (uOff + 2 * w) / tw, v0, (uOff + 2 * w + d) / tw, v0, (uOff + 2 * w + d) / tw, v1, (uOff + 2 * w) / tw, v1, r, g, b);
  // -x face: u along +z
  soup.quad(x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0, (uOff + 2 * w + d) / tw, v0, (uOff + 2 * w + 2 * d) / tw, v0, (uOff + 2 * w + 2 * d) / tw, v1, (uOff + 2 * w + d) / tw, v1, r, g, b);
}

export interface NightItem {
  (f: number): void;
}

export interface BuildCtx {
  rng: Rng;
  scene: THREE.Scene;
  tex: Textures;
  layout: Layout;
  /** vertex-coloured buckets */
  solid: Bucket;
  metal: Bucket;
  glass: Bucket;
  /** floodlit stone (warm emissive at night) */
  stoneLit: Bucket;
  /** warm emissive surfaces (interior ceilings, lamps) */
  lit: Bucket;
  redLit: Bucket;
  blueLit: Bucket;
  /** textured soups keyed by facade style (+ 'shop') */
  facades: Map<string, QuadSoup>;
  roofs: QuadSoup;
  colliders: AABB[];
  buildingRects: AABB[];
  raycast: THREE.Object3D[];
  pois: PointOfInterest[];
  parking: { pos: THREE.Vector3; heading: number }[];
  nightItems: NightItem[];
  updaters: ((dt: number, t: number) => void)[];
  lamps: { x: number; z: number }[];
  /** neon sign placements requested by landmarks (built by props) */
  neonRequests: { x: number; y: number; z: number; ry: number; w: number; idx: number }[];
  signRequests: { x: number; y: number; z: number; ry: number; w: number; idx: number }[];
  billboardRequests: { x: number; y: number; z: number; ry: number; w: number; idx?: number }[];
  treeRequests: { x: number; z: number; s: number }[];
  benchRequests: { x: number; z: number; ry: number }[];
  /** extra lamp posts (bridges, plazas): ry = direction the arm points to */
  lampRequests: { x: number; z: number; ry: number }[];
  /** string-light bulbs + wire polylines (flat xyz triplets, consecutive pairs) */
  bulbRequests: { x: number; y: number; z: number }[];
  wires: number[];
  containerRequests: { x: number; y: number; z: number; ry: number; color: number }[];
  /** flat grass patches (parks, pitches) */
  parkSoup: QuadSoup;
  /** market hall roof (patterned) */
  marketSoup: QuadSoup;
}

export function facadeSoup(ctx: BuildCtx, key: string): QuadSoup {
  let s = ctx.facades.get(key);
  if (!s) {
    s = new QuadSoup('facade_' + key);
    ctx.facades.set(key, s);
  }
  return s;
}

export function aabb(minX: number, minZ: number, maxX: number, maxZ: number, height: number): AABB {
  return { minX, minZ, maxX, maxZ, height };
}

export const C = {
  stoneCream: 0xe8dcc2,
  stoneGrey: 0xa9a49a,
  stoneDark: 0x6e6a62,
  copperGreen: 0x4f8a72,
  roofRed: 0x7a3b2e,
  roofDark: 0x3a3a40,
  white: 0xf2f2ee,
  ochre: 0xd9b25c,
  yellow: 0xe8c860,
  concrete: 0x9a9892,
  concreteDark: 0x6b6a66,
  steelDark: 0x2c2f35,
  steelGreen: 0x2f6b4f,
  steelBlue: 0x3b5b8a,
  woodDark: 0x4a3626,
  glassBlue: 0x2a3b4c,
  trunk: 0x5a4030,
  leaf: 0x3f7a2f,
  black: 0x111114,
};
